import React, { useEffect, useState } from "react";
import { Loader2, X, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/ui/components/ui/dialog";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { GiphyFetch } from "@giphy/js-fetch-api";
import { appConfig } from "@/app/module_api";
import { ErrorLogger } from "@/utils/module_api";

let giphyFetch: GiphyFetch;

function GiphySearch({
  onGifClick,
  gifCatSearch,
  gifSearchText,
  isLoading,
  setIsLoading,
  forceRefresh,
}: {
  onGifClick: (args: any) => void;
  gifCatSearch: string;
  gifSearchText?: string;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  forceRefresh: number;
}) {
  const [gifs, setGifs] = useState<any[]>([]);

  const fetchGifsTrending = async (offset: number = 0) => {
    try {
      const result = await giphyFetch.trending({ offset, limit: 24 });
      return result.data;
    } catch (error) {
      ErrorLogger.logError(error as Error, "GiphyPopup.tsx");
      console.error("Error fetching trending GIFs:", error);
      return [];
    }
  };

  const fetchGifs = async (offset: number = 0) => {
    try {
      const query = gifCatSearch === "search" ? gifSearchText : gifCatSearch;
      const result = await giphyFetch.search(query || "", {
        offset,
        limit: 24,
      });
      return result.data;
    } catch (error) {
      ErrorLogger.logError(error as Error, "GiphyPopup.tsx");
      console.error("Error searching GIFs:", error);
      return [];
    }
  };

  useEffect(() => {
    if (!giphyFetch && appConfig && appConfig.giphyApiKey) {
      giphyFetch = new GiphyFetch(appConfig.giphyApiKey);
    }
  }, []);

  const loadGifs = async () => {
    if (!giphyFetch) {
      if (appConfig && appConfig.giphyApiKey) {
        giphyFetch = new GiphyFetch(appConfig.giphyApiKey);
      } else {
        console.error("Missing Giphy API key in appConfig");
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);
    try {
      let fetchedGifs;
      if (gifCatSearch === "trending") {
        fetchedGifs = await fetchGifsTrending();
      } else if (
        gifCatSearch === "search" &&
        (!gifSearchText || gifSearchText.trim() === "")
      ) {
        fetchedGifs = await fetchGifsTrending();
      } else {
        fetchedGifs = await fetchGifs();
      }
      setGifs(fetchedGifs);
    } catch (error) {
      ErrorLogger.logError(error as Error, "GiphyPopup.tsx");
      console.error("Error loading GIFs:", error);
      setGifs([]);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    }
  };

  useEffect(() => {
    loadGifs();
  }, [gifCatSearch, gifSearchText, forceRefresh]);

  return (
    <React.Fragment>
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="w-10 h-10 animate-spin mb-4" />
          <div>Loading GIFs...</div>
        </div>
      ) : gifCatSearch === "search" && !gifSearchText && !gifs.length ? (
        <div className="text-center py-10 text-muted-foreground">
          Start typing to search for GIFs...
        </div>
      ) : gifs.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          No GIFs found. Try another search!
        </div>
      ) : null}

      {!isLoading && gifs.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1 pb-4">
          {gifs.map((gif, index) => (
            <div
              key={index}
              className="aspect-square overflow-hidden rounded-lg bg-muted cursor-pointer hover:scale-105 transition-transform"
            >
              <img
                src={gif.images.fixed_height.url}
                alt="GIF"
                onClick={() => onGifClick({ gif })}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      )}
    </React.Fragment>
  );
}

interface GiphyPopupProps {
  opened: boolean;
  onPopupClose: () => void;
  onGifSelect: (gifUrl: string) => void;
  title?: string;
}

const GiphyPopup: React.FC<GiphyPopupProps> = ({
  opened,
  onPopupClose,
  onGifSelect,
  title = "Choose a GIF",
}) => {
  const [gifCatSearch, setGifCatSearch] = useState("trending");
  const [gifSearchText, setGifSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [refreshCounter, setRefreshCounter] = useState(0);

  const onGifClick = ({ gif }: any) => {
    const src = gif.images.fixed_height.url.split("?")[0];
    onGifSelect(src);
    onPopupClose();
  };

  const refreshGifs = () => {
    setIsLoading(true);
    setRefreshCounter((prev) => prev + 1);
  };

  useEffect(() => {
    if (opened) {
      setIsLoading(true);
      setGifCatSearch("trending");
      setGifSearchText("");
      setSearchValue("");
      setTimeout(() => {
        refreshGifs();
      }, 300);
    }
  }, [opened]);

  const handleSearch = () => {
    if (searchValue.trim()) {
      setIsLoading(true);
      setGifCatSearch("search");
      setGifSearchText(searchValue);
      refreshGifs();
    }
  };

  const categories = ["TRENDING", "HAHA", "SAD", "LOVE", "REACTION"];

  return (
    <Dialog open={opened} onOpenChange={(open) => !open && onPopupClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {/* Search box */}
        <div className="px-4 py-2 border-b">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search GIFs..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                className="pl-10 pr-8"
              />
              {searchValue && (
                <button
                  onClick={() => {
                    setSearchValue("");
                    setIsLoading(true);
                    setGifCatSearch("trending");
                    setGifSearchText("");
                    refreshGifs();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button onClick={handleSearch}>Search</Button>
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex overflow-x-auto px-4 py-2 gap-2 border-b">
          {categories.map((category) => {
            const isActive = gifCatSearch === category.toLowerCase();
            return (
              <Button
                key={category}
                variant={isActive ? "default" : "ghost"}
                size="sm"
                onClick={() => {
                  setIsLoading(true);
                  setGifSearchText("");
                  setGifCatSearch(category.toLowerCase());
                  refreshGifs();
                }}
                className="whitespace-nowrap"
              >
                {category}
              </Button>
            );
          })}
        </div>

        {/* GIF grid */}
        <div className="flex-1 overflow-auto p-4">
          {opened && (
            <GiphySearch
              gifSearchText={gifSearchText}
              gifCatSearch={gifCatSearch}
              onGifClick={onGifClick}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              forceRefresh={refreshCounter}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GiphyPopup;
